
import { supabase } from '@/integrations/supabase/client';
import { CertificateReport, ReportStatus } from './types';
import { markCertificatesAsReported } from './verificationUtils';

/**
 * Saves a report to the database for audit and retention purposes
 */
export async function saveReportRecord(
  year: number,
  quarter: number,
  state: string,
  reportData: string,
  format: string
): Promise<{ success: boolean; error: any; reportId?: string }> {
  try {
    // Create a report object that matches what Supabase expects
    const insertData: CertificateReport = {
      year,
      quarter,
      state,
      report_data: reportData,
      format,
      generated_at: new Date().toISOString(),
      generated_by: (await supabase.auth.getUser()).data?.user?.id,
      status: ReportStatus.DRAFT // Use the enum value instead of a string
    };
    
    const { data, error } = await supabase
      .from('certificate_reports')
      .insert(insertData)
      .select('id')
      .single();
      
    if (error) throw error;
    
    // Extract certificate numbers from the report data
    // This is a simple extraction that assumes certificate numbers are in a specific format
    // You may need to adjust this based on your actual report format
    const certificateNumberRegex = /[A-Z]{2}\d{8}/g; // Example: FL12345678
    const certificateNumbers = reportData.match(certificateNumberRegex) || [];
    
    // Mark these certificates as reported
    if (certificateNumbers.length > 0 && data?.id) {
      await markCertificatesAsReported(certificateNumbers, data.id);
    }
    
    return { success: true, error: null, reportId: data?.id };
  } catch (error) {
    console.error("Error saving report record:", error);
    return { success: false, error };
  }
}

/**
 * Retrieves report history from the database
 */
export async function getReportHistory(
  state?: string,
  year?: number,
  quarter?: number
): Promise<{ data: CertificateReport[]; error: any }> {
  try {
    let query = supabase
      .from('certificate_reports')
      .select('*')
      .order('generated_at', { ascending: false });
    
    if (state) {
      query = query.eq('state', state);
    }
    
    if (year) {
      query = query.eq('year', year);
    }
    
    if (quarter) {
      query = query.eq('quarter', quarter);
    }
    
    const { data, error } = await query;
    
    if (error) throw error;
    
    // Convert string status to enum ReportStatus before returning
    const typedData: CertificateReport[] = data?.map(report => ({
      ...report,
      status: report.status as ReportStatus
    })) || [];
    
    return { data: typedData, error: null };
  } catch (error) {
    console.error("Error fetching report history:", error);
    return { data: [], error };
  }
}

/**
 * Updates the status of a report
 */
export async function updateReportStatus(
  reportId: string,
  status: ReportStatus
): Promise<{ success: boolean; error: any }> {
  try {
    const { error } = await supabase
      .from('certificate_reports')
      .update({ status })
      .eq('id', reportId);
      
    if (error) throw error;
    
    return { success: true, error: null };
  } catch (error) {
    console.error("Error updating report status:", error);
    return { success: false, error };
  }
}

/**
 * Gets statistics about reports for a specific state
 */
export async function getReportStatistics(
  state?: string
): Promise<{ data: any; error: any }> {
  try {
    // Get total reports
    const { data: totalReports, error: totalError } = await supabase
      .from('certificate_reports')
      .select('id', { count: 'exact' })
      .eq(state ? 'state' : 'id', state || 'id');
      
    if (totalError) throw totalError;
    
    // Get reports by status
    const statuses = Object.values(ReportStatus);
    const reportsByStatus: Record<string, number> = {};
    
    for (const status of statuses) {
      const { data, error } = await supabase
        .from('certificate_reports')
        .select('id', { count: 'exact' })
        .eq('status', status)
        .eq(state ? 'state' : 'id', state || 'id');
        
      if (error) throw error;
      
      reportsByStatus[status] = data?.length || 0;
    }
    
    return {
      data: {
        totalReports: totalReports?.length || 0,
        byStatus: reportsByStatus
      },
      error: null
    };
  } catch (error) {
    console.error("Error getting report statistics:", error);
    return { data: null, error };
  }
}
